import { supabase } from '../config/supabase'

/**
 * Obtiene el wedding_id del usuario (considerando si es partner)
 */
const getUserWeddingId = async (userId) => {
  try {
    // Primero, obtener el perfil del usuario para verificar si es partner
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('partner_user_id')
      .eq('id', userId)
      .maybeSingle()

    if (profileError && profileError.code !== 'PGRST116') {
      console.error('Error fetching profile:', profileError)
    }

    // Determinar el user_id de la boda:
    // - Si el usuario tiene un partner_user_id (es partner), buscar la boda del usuario principal (partner_user_id)
    // - Si el usuario NO tiene partner_user_id pero hay otro perfil que tiene este usuario como partner_user_id,
    //   buscar la boda del usuario mismo (es el usuario principal)
    // - Si no tiene partner, buscar la boda del usuario mismo
    let weddingOwnerId = userId
    
    if (profile?.partner_user_id) {
      // Este usuario es un partner, buscar la boda del usuario principal
      weddingOwnerId = profile.partner_user_id
    } else {
      // Verificar si hay otro perfil que tenga este usuario como partner
      // Si es así, este usuario es el principal y debe buscar su propia boda
      const { data: partnerProfile } = await supabase
        .from('profiles')
        .select('id')
        .eq('partner_user_id', userId)
        .maybeSingle()
      
      // Si no hay partner que apunte a este usuario, buscar su propia boda
      weddingOwnerId = userId
    }

    const { data, error } = await supabase
      .from('weddings')
      .select('id')
      .eq('user_id', weddingOwnerId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar 406

    if (error) {
      if (error.code === 'PGRST116') {
        return null
      }
      console.error('Error fetching wedding ID:', error)
      return null
    }

    return data?.id || null
  } catch (error) {
    console.error('Error fetching wedding ID:', error)
    return null
  }
}

/**
 * Obtiene o crea la invitación RSVP para el usuario
 */
export const getOrCreateRSVPInvite = async (userId) => {
  try {
    const weddingId = await getUserWeddingId(userId)
    if (!weddingId) {
      throw new Error('No wedding found for user')
    }

    // Obtener perfil del usuario para nombres
    const { data: profile } = await supabase
      .from('profiles')
      .select('first_name, last_name, partner_first_name, partner_last_name, wedding_date')
      .eq('id', userId)
      .single()

    // Obtener boda para fecha y lugar
    const { data: wedding } = await supabase
      .from('weddings')
      .select('event_date, name')
      .eq('id', weddingId)
      .single()

    // IMPORTANTE: Siempre buscar primero si existe una invitación para esta boda
    // Esto previene crear duplicados
    console.log('🔵 getOrCreateRSVPInvite - Buscando invitación existente para wedding_id:', weddingId)
    const { data: existingInvite, error: fetchError } = await supabase
      .from('rsvp_invites')
      .select('*')
      .eq('wedding_id', weddingId)
      .maybeSingle()

    if (fetchError && fetchError.code !== 'PGRST116') {
      console.error('❌ Error buscando invitación existente:', fetchError)
      // Continuar para intentar crear una nueva si el error no es "no encontrado"
    }

    if (existingInvite) {
      console.log('✅ Invitación existente encontrada:', existingInvite.id)
      return existingInvite
    }

    console.log('📝 No se encontró invitación existente, creando nueva...')

    // Crear nueva invitación solo si no existe una
    // NOTA: El constraint UNIQUE en wedding_id previene duplicados a nivel de base de datos
    const rsvpSlug = `rsvp-${Date.now()}-${Math.random().toString(36).substring(7)}`
    const { data: newInvite, error: createError } = await supabase
      .from('rsvp_invites')
      .insert([
        {
          wedding_id: weddingId,
          rsvp_slug: rsvpSlug,
          partner1_name: profile?.first_name && profile?.last_name 
            ? `${profile.first_name} ${profile.last_name}`.trim()
            : '',
          partner2_name: profile?.partner_first_name && profile?.partner_last_name
            ? `${profile.partner_first_name} ${profile.partner_last_name}`.trim()
            : '',
          wedding_date: wedding?.event_date || profile?.wedding_date || null,
          wedding_venue: '',
          title: '',
          message: '',
          image_url: null,
          web_enabled: true,
          share_enabled: false,
        },
      ])
      .select()
      .maybeSingle() // Usar maybeSingle() en lugar de single() para evitar errores

    if (createError) {
      // Si ya existe una invitación para esta boda (violación de constraint único), obtener la existente
      if (createError.code === '23505' && (createError.message?.includes('wedding_id') || createError.message?.includes('unique'))) {
        console.warn('⚠️ RSVP invite already exists for this wedding (constraint violation). Fetching existing invite.')
        
        // Intentar obtener la invitación existente nuevamente
        const { data: existingInviteRetry, error: retryError } = await supabase
          .from('rsvp_invites')
          .select('*')
          .eq('wedding_id', weddingId)
          .maybeSingle()
        
        if (retryError) {
          console.error('❌ Error obteniendo invitación existente después de constraint violation:', retryError)
          throw createError
        }
        
        if (existingInviteRetry) {
          console.log('✅ Invitación existente obtenida después de constraint violation:', existingInviteRetry.id)
          return existingInviteRetry
        }
      }
      
      // Si la tabla no existe, retornar un objeto mock para desarrollo
      if (createError.code === '42P01') {
        console.warn('rsvp_invites table does not exist. Using mock data.')
        return {
          id: 'mock',
          wedding_id: weddingId,
          rsvp_slug: rsvpSlug,
          partner1_name: profile?.first_name && profile?.last_name 
            ? `${profile.first_name} ${profile.last_name}`.trim()
            : '',
          partner2_name: profile?.partner_first_name && profile?.partner_last_name
            ? `${profile.partner_first_name} ${profile.partner_last_name}`.trim()
            : '',
          wedding_date: wedding?.event_date || profile?.wedding_date || null,
          wedding_venue: '',
          title: '',
          message: '',
          image_url: null,
          web_enabled: true,
          share_enabled: false,
        }
      }
      
      console.error('❌ Error creando nueva invitación:', createError)
      throw createError
    }

    if (!newInvite) {
      // Si no se creó la invitación pero tampoco hubo error, intentar obtener la existente una vez más
      console.warn('⚠️ newInvite es null, intentando obtener invitación existente nuevamente...')
      const { data: existingInviteFinal } = await supabase
        .from('rsvp_invites')
        .select('*')
        .eq('wedding_id', weddingId)
        .maybeSingle()
      
      if (existingInviteFinal) {
        console.log('✅ Invitación existente encontrada en retry final:', existingInviteFinal.id)
        return existingInviteFinal
      }
      
      throw new Error('Failed to create or retrieve RSVP invite')
    }

    console.log('✅ Nueva invitación creada:', newInvite.id)
    return newInvite
  } catch (error) {
    console.error('Error getting or creating RSVP invite:', error)
    throw error
  }
}

/**
 * Actualiza la invitación RSVP
 */
export const updateRSVPInvite = async (inviteId, updates) => {
  try {
    console.log('🔵 updateRSVPInvite - INICIANDO UPDATE')
    console.log('🔵 inviteId:', inviteId)
    console.log('🔵 updates:', JSON.stringify(updates, null, 2))
    
    // Hacer el UPDATE sin .select() para evitar el error 406
    const { error: updateError } = await supabase
      .from('rsvp_invites')
      .update(updates)
      .eq('id', inviteId)

    if (updateError) {
      console.error('❌ Error updating RSVP invite:', updateError)
      // Si la tabla no existe, solo loguear el error
      if (updateError.code === '42P01') {
        console.warn('rsvp_invites table does not exist. Update skipped.')
        return { ...updates, id: inviteId }
      }
      throw updateError
    }

    console.log('✅ UPDATE exitoso, esperando 200ms...')

    // Esperar un momento para asegurar que el UPDATE se haya completado
    await new Promise(resolve => setTimeout(resolve, 200))

    // Después del UPDATE exitoso, hacer un SELECT separado para obtener los datos actualizados
    console.log('🔵 Ejecutando SELECT para obtener datos actualizados...')
    const { data, error: selectError } = await supabase
      .from('rsvp_invites')
      .select('*')
      .eq('id', inviteId)
      .maybeSingle()

    if (selectError) {
      console.error('❌ Error fetching updated invite:', selectError)
      throw new Error('Invite was updated but could not be retrieved. Please refresh the page.')
    }

    if (!data) {
      console.error('❌ No se encontró la invitación después del UPDATE')
      throw new Error('Invite not found after update. This may be a permissions issue. Please check your RLS policies.')
    }

    console.log('✅ SELECT exitoso, datos obtenidos:', data)
    return data
  } catch (error) {
    console.error('❌ Error updating RSVP invite:', error)
    throw error
  }
}

/**
 * Obtiene la invitación por slug (página pública)
 * Esta función NO requiere autenticación y debe funcionar para usuarios públicos
 */
export const getRSVPInviteBySlug = async (slug) => {
  try {
    if (!slug || slug.trim() === '') {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Empty slug provided')
      }
      return null
    }

    // Primero intentar obtener solo los datos de rsvp_invites (sin join)
    // Esto evita problemas con políticas RLS de weddings
    // IMPORTANTE: Usar un cliente sin autenticación para acceso público
    const { data, error } = await supabase
      .from('rsvp_invites')
      .select('*')
      .eq('rsvp_slug', slug.trim())
      .eq('web_enabled', true) // Asegurar que solo se obtengan invitaciones habilitadas
      .maybeSingle()

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error fetching RSVP invite:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
      }
      
      // Si es un error de "no encontrado", retornar null
      if (error.code === 'PGRST116' || error.code === '42P01') {
        return null
      }
      
      // Para otros errores, intentar de nuevo sin maybeSingle
      const { data: retryData, error: retryError } = await supabase
        .from('rsvp_invites')
        .select('*')
        .eq('rsvp_slug', slug.trim())
        .eq('web_enabled', true) // También incluir el filtro web_enabled en el retry
        .limit(1)
      
      if (retryError) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Retry also failed:', retryError)
        }
        return null
      }
      
      if (!retryData || retryData.length === 0) {
        return null
      }
      
      return retryData[0]
    }

    if (!data) {
      return null
    }

    // Intentar obtener datos de wedding si es necesario (opcional, puede fallar por RLS)
    try {
      if (data.wedding_id) {
        const { data: weddingData } = await supabase
          .from('weddings')
          .select('id, user_id, name, event_date')
          .eq('id', data.wedding_id)
          .maybeSingle()

        if (weddingData) {
          data.weddings = weddingData
          
          // Intentar obtener perfil (puede fallar por RLS, no es crítico)
          if (weddingData.user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('first_name, last_name, partner_first_name, partner_last_name')
              .eq('id', weddingData.user_id)
              .maybeSingle()

            if (profile) {
              data.weddings.profiles = profile
            }
          }
        }
      }
    } catch (weddingError) {
      // No es crítico si no podemos obtener datos de wedding
      if (process.env.NODE_ENV === 'development') {
        console.warn('Could not fetch wedding data (may be RLS restriction):', weddingError)
      }
    }

    return data
  } catch (error) {
    console.error('Unexpected error fetching RSVP invite by slug:', error)
    return null
  }
}

/**
 * Busca invitados por nombre, apellido o email
 */
export const searchGuests = async (weddingId, searchTerm) => {
  try {
    if (!searchTerm || searchTerm.trim() === '') {
      return []
    }

    if (!weddingId) {
      if (process.env.NODE_ENV === 'development') {
        console.error('searchGuests: No weddingId provided')
      }
      return []
    }

    const term = searchTerm.toLowerCase().trim()
    
    const { data, error } = await supabase
      .from('guests')
      .select('id, first_name, last_name, email, full_name')
      .eq('wedding_id', weddingId)
      .or(`first_name.ilike.%${term}%,last_name.ilike.%${term}%,email.ilike.%${term}%,full_name.ilike.%${term}%`)
      .order('first_name', { ascending: true })
      .limit(20)

    if (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('searchGuests: Supabase error:', error)
        console.error('Error code:', error.code)
        console.error('Error message:', error.message)
        console.error('Error details:', error.details)
        console.error('Error hint:', error.hint)
        
        // Si es un error de permisos RLS, mostrar mensaje más claro
        if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('policy')) {
          console.error('RLS Policy Error: The guests table may not have public read access. Please run RLS_POLICIES.sql in Supabase.')
        }
      }
      
      throw error
    }

    return data || []
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('searchGuests: Unexpected error:', error)
    }
    return []
  }
}

/**
 * Obtiene invitado por ID
 */
export const getGuestById = async (guestId) => {
  try {
    const { data, error } = await supabase
      .from('guests')
      .select('*')
      .eq('id', guestId)
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error fetching guest by ID:', error)
    return null
  }
}

/**
 * Actualiza el RSVP de un invitado (incluye respuestas a preguntas custom)
 */
export const updateGuestRSVP = async (guestId, rsvpData) => {
  try {
    const updates = {
      rsvp_status: rsvpData.attending === true ? 'confirmed' : rsvpData.attending === false ? 'declined' : undefined,
      dietary_requirements: rsvpData.dietary_requirements ?? null,
      allergies: (rsvpData.allergies && rsvpData.allergies.trim()) ? rsvpData.allergies.trim() : null,
      notes: (rsvpData.notes && rsvpData.notes.trim()) ? rsvpData.notes.trim() : null,
    }
    if (updates.rsvp_status === undefined) delete updates.rsvp_status
    if (rsvpData.custom_rsvp_answers != null && typeof rsvpData.custom_rsvp_answers === 'object') {
      updates.custom_rsvp_answers = rsvpData.custom_rsvp_answers
    }

    const { data, error } = await supabase
      .from('guests')
      .update(updates)
      .eq('id', guestId)
      .select()
      .single()

    if (error) throw error

    return data
  } catch (error) {
    console.error('Error updating guest RSVP:', error)
    throw error
  }
}
